import {
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DollarCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  FallOutlined,
  FileTextOutlined,
  LockOutlined,
  UnlockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import * as XLSX from "xlsx";
import React, { useContext, useEffect, useState } from "react";
import apiPath from "../../constants/apiPath";
import useDebounce from "../../hooks/useDebounce";
import useRequest from "../../hooks/useRequest";
import { Severty, ShowToast } from "../../helper/toast";

const { TabPane } = Tabs;
const { Option } = Select;

const formatCurrency = (value) =>
  typeof value === "number" ? `₦${value.toLocaleString()}` : "₦0";

const getStatusTag = (status) => {
  switch (status) {
    case "pending":
      return <Tag color="orange">pending</Tag>;
    case "rejected":
      return <Tag color="red">rejected</Tag>;
    case "approve":
      return <Tag color="green">approved</Tag>;
    default:
      return <Tag>{status}</Tag>;
  }
};
const { Title, Text } = Typography;

const WalletPaymentManager = () => {
  const { request } = useRequest();
  const [activeTab, setActiveTab] = useState("wallets");
  const [isExporting, setIsExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const debouncedSearchText = useDebounce(searchText, 300);
  const [modal, contextHolder] = Modal.useModal();
  const [processingId, setProcessingId] = useState(null);
  const [documentModal, setDocumentModal] = useState({
    visible: false,
    documentUrl: null,
    documentType: null,
    userId: null,
  });

  const columns = {
    wallets: [
      {
        title: "User",
        render: (_, r) => (
          <div>
            <strong>{r.userName || "User"}</strong>
            <br />
            <small>{r.email}</small>
            <br />
          </div>
        ),
      },
      { title: "Bonus", dataIndex: "bonus", render: formatCurrency },
      { title: "Winnings", dataIndex: "winnings", render: formatCurrency },
      { title: "Referral", dataIndex: "referral", render: formatCurrency },
      {
        title: "Total",
        dataIndex: "total",
        render: (val) => <strong>{formatCurrency(val)}</strong>,
      },
      // {
      //   title: "Status",
      //   render: (_, r) => (
      //     <Space>
      //       {getStatusTag(r.kycStatus)}
      //       {r.isLocked ? (
      //         <LockOutlined style={{ color: "red" }} />
      //       ) : (
      //         <UnlockOutlined style={{ color: "green" }} />
      //       )}
      //     </Space>
      //   ),
      // },
      // {
      //   title: "Actions",
      //   render: () => (
      //     <Tooltip title="View">
      //       <Button icon={<EyeOutlined />} type="link" />
      //     </Tooltip>
      //   ),
      // },
    ],
    transactions: [
      {
        title: "User",
        render: (_, r) => (
          <div>
            <strong>{r.userName || "User"}</strong>
            <br />
            <small>{r.transaction_id}</small>
          </div>
        ),
      },
      {
        title: "Transaction Type",
        dataIndex: "transaction_type",
        render: (t) => <span>{t ? t.replace("_", " ") : "-"}</span>,
      },
      {
        title: "Type",
        dataIndex: "type",
        render: (t) => <span>{t ? t.replace("_", " ") : "-"}</span>,
      },
      { title: "Amount", dataIndex: "amount", render: formatCurrency },
      { title: "Status", dataIndex: "status", render: getStatusTag },
      {
        title: "Date",
        dataIndex: "date",
        render: (date, record) => {
          return record?.date || " - ";
        },
      },
      // {
      //   title: "Actions",
      //   render: () => (
      //     <Tooltip title="View">
      //       <Button icon={<EyeOutlined />} type="link" />
      //     </Tooltip>
      //   ),
      // },
    ],
    withdrawals: [
      {
        title: "User",
        render: (_, r) => (
          <div>
            <strong>{r.userName || "User"}</strong>
            <br />
            <small>{r.email}</small>
            <br />
          </div>
        ),
      },
      { title: "Amount", dataIndex: "amount", render: formatCurrency },
      { title: "Bank Details", dataIndex: "bank" },
      {
        title: "Wallet Balance",
        render: (_, r) => formatCurrency(r.wallet_amount),
      },
      {
        title: "Status",
        render: (_, r) => (
          <Space>
            {getStatusTag(r.status)}
            {r.totalWalletBalance >= 10000 ? (
              <Tooltip title="Balance sufficient for withdrawal">
                <UnlockOutlined style={{ color: "green" }} />
              </Tooltip>
            ) : (
              <Tooltip title="Insufficient balance (less than N10,000)">
                <LockOutlined style={{ color: "red" }} />
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: "Date",
        dataIndex: "date",
        render: (date, record) => {
          return record?.date || " - ";
        },
      },
      {
        title: "Actions",
        render: (_, r) => {
          const showActions = ["pending", "Pending"].includes(r.status);
          const isProcessing = processingId === (r._id || r.userId);

          return (
            <Space>
              {showActions && (
                <>
                  <Tooltip title="Approve">
                    <Button
                      type="text"
                      icon={
                        <CheckCircleOutlined
                          style={{ color: "#52c41a", fontSize: "18px" }}
                        />
                      }
                      onClick={() =>
                        !isProcessing && showWithdrawConfirm("approve", r)
                      }
                      loading={isProcessing}
                      disabled={isProcessing}
                    />
                  </Tooltip>
                  <Tooltip title="Reject">
                    <Button
                      type="text"
                      icon={
                        <CloseCircleOutlined
                          style={{ color: "#ff4d4f", fontSize: "18px" }}
                        />
                      }
                      onClick={() =>
                        !isProcessing && showWithdrawConfirm("reject", r)
                      }
                      loading={isProcessing}
                      disabled={isProcessing}
                    />
                  </Tooltip>
                </>
              )}
            </Space>
          );
        },
      },
    ],
    kyc: [
      {
        title: "User",
        render: (_, r) => (
          <div>
            <strong>{r.userName || "User"}</strong>
            <br />
            <small>{r.email}</small>
            <br />
            <small>{r.userId}</small>
          </div>
        ),
      },
      {
        title: "Bank Details",
        render: (_, r) => (
          <div>
            <strong>{r.bankDetails?.bankName || "-"}</strong>
            <br />
            <small>A/C: {r.bankDetails?.accountNumber || "-"}</small>
            <br />
            <small>IFSC: {r.bankDetails?.ifscCode || "-"}</small>
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "kycStatus",
        render: getStatusTag,
      },
      {
        title: "Documents",
        render: (_, r) => (
          <Space>
            <Tooltip title="View NIN Document">
              <Button
                icon={<EyeOutlined />}
                type="link"
                onClick={() => showDocument(r.userId, "nin")}
                disabled={!r.nin_number}
              />
            </Tooltip>
            <Tooltip title="View Passport">
              <Button
                icon={<EyeOutlined />}
                type="link"
                onClick={() => showDocument(r.userId, "passport")}
                disabled={!r.passport}
              />
            </Tooltip>
          </Space>
        ),
      },
      {
        title: "Actions",
        render: (_, r) => (
          <Space>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => handleKycAction(r.userId, "approve")}
            >
              Approve
            </Button>
            <Button
              type="primary"
              danger
              icon={<CloseOutlined />}
              onClick={() => handleKycAction(r.userId, "rejected")}
            >
              Reject
            </Button>
          </Space>
        ),
      },
    ],
  };

  const showConfirm = (action, userId) => {
    let reason = ""; // To store rejection reason

    Modal.confirm({
      title: `Confirm KYC ${action === "approve" ? "Approval" : "Rejection"}`,
      icon:
        action === "approve" ? (
          <CheckCircleOutlined />
        ) : (
          <CloseCircleOutlined />
        ),
      content: (
        <div>
          <p>Are you sure you want to {action} this KYC verification?</p>
          {/* {action === "reject" && (
            <Input.TextArea
              placeholder="Reason for rejection"
              rows={3}
              onChange={(e) => (reason = e.target.value)}
              style={{ marginTop: 10 }}
            />
          )} */}
        </div>
      ),
      okText: `Confirm ${action}`,
      okButtonProps: {
        type: "primary",
        danger: action === "reject",
      },
      cancelText: "Cancel",
      onOk: () => {
        handleKycAction(
          userId,
          action,
          action === "reject" ? reason : undefined
        );
      },
    });
  };

  const showDocument = (userId, docType) => {
    const userKyc = list.find((item) => item.userId === userId);
    if (!userKyc) return;

    setDocumentModal({
      visible: true,
      documentUrl: docType === "nin" ? userKyc?.nin_number : userKyc?.passport,
      documentType: docType === "nin" ? "NIN Document" : "Passport",
      userId,
    });
  };

  const filteredData = (data) => {
    return data.filter((item) => {
      const matchSearch =
        item?.name?.toLowerCase().includes(search.toLowerCase()) ||
        (item?.email &&
          item?.email?.toLowerCase().includes(search.toLowerCase())) ||
        item?.userId?.toLowerCase().includes(search.toLowerCase());

      const matchStatus =
        statusFilter === "all" || item.status === statusFilter;
      const matchType = typeFilter === "all" || item.type === typeFilter;

      return matchSearch && matchStatus && matchType;
    });
  };

  useEffect(() => {
    setLoading(true);
    setList([]);
    const defaultPagination = { current: 1, pageSize: 10 };
    setPagination(defaultPagination);
    fetchData(defaultPagination);
  }, [refresh, debouncedSearchText, activeTab, statusFilter, typeFilter]);

  const fetchData = (pagination, filters) => {
    const filterActive = filters ? filters.is_active : null;
    request({
      url:
        apiPath.getWalletPayment +
        `?activeTab=${activeTab}&status=${statusFilter}&type=${typeFilter}&page=${
          pagination ? pagination.current : 1
        }&pageSize=${
          pagination ? pagination.pageSize : 10
        }&search=${debouncedSearchText}`,
      method: "GET",
      onSuccess: ({ data, status }) => {
        setLoading(false);
        if (status) {
          let normalized = [];

          if (activeTab === "wallets") {
            normalized = (data.wallets || []).map((w) => ({
              ...w,
            }));
          } else if (activeTab === "transactions") {
            console.log(data?.transactions);

            normalized = (data.transactions || []).map((t) => ({
              ...t,
              date: t.date ? new Date(t.date).toLocaleString() : "-",
              userId: t.userId || "-",
              status: t.status || "completed",
            }));
          } else if (activeTab === "withdrawals") {
            normalized = (data.withdrawals || []).map((w) => ({
              ...w,
              date: w.date ? new Date(w.date).toLocaleString() : "-",
              userId: w.userId || "-",
              bank: w.bank || "-",
              status: w.status || "Pending",
            }));
          } else if (activeTab === "kyc") {
            normalized = (data.kycVerifications || []).map((k) => ({
              ...k,
              status: k.kycStatus || "Pending",
              bankDetails: k.bankDetails || {
                bankName: "-",
                accountNumber: "-",
                ifscCode: "-",
              },
            }));
          }
          setList(normalized);
          setPagination({
            current: pagination?.current || 1,
            total: data.pagination?.total || 0,
            pageSize: pagination?.pageSize || 10,
          });
        }
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleKycAction = (userId, action, reason) => {
    request({
      url: `/admin/wallet-payment/approve-reject-kyc/${userId}`,
      method: "PUT",
      data: {
        action: action,
        ...(reason && { reason }),
      },
      onSuccess: () => {
        ShowToast(`KYC ${action} successfully`, Severty.SUCCESS);
        setRefresh(!refresh);
      },
      onError: (error) => {
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleWithdrawAction = (withdrawal, action, remark) => {
    console.log("Processing:", withdrawal._id, action);
    setProcessingId(withdrawal._id || withdrawal.userId);

    request({
      url: "/admin/wallet-payment/approve-reject-request",
      method: "PUT",
      data: {
        request_id: withdrawal._id || withdrawal.userId,
        action: action,
        remark: remark,
      },
      onSuccess: () => {
        console.log("Success:", action); // Debug log
        ShowToast(`Withdrawal ${action}d successfully`, Severty.SUCCESS);
        setRefresh(!refresh);
        setProcessingId(null);
      },
      onError: (error) => {
        console.error("Error:", error); // Debug log
        ShowToast(error, Severty.ERROR);
        setProcessingId(null);
      },
    });
  };

  const showWithdrawConfirm = (action, withdrawal) => {
    console.log("Action:", action, "Withdrawal:", withdrawal);
    let remark = "";

    modal.confirm({
      title: `Confirm Withdrawal ${
        action === "approve" ? "Approval" : "Rejection"
      }`,
      icon:
        action === "approve" ? (
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
        ),
      content: (
        <div style={{ margin: "16px 0" }}>
          <p>Are you sure you want to {action} this withdrawal request?</p>
          <p>
            <strong>Amount:</strong> {formatCurrency(withdrawal.amount)}
          </p>
          <p>
            <strong>User:</strong> {withdrawal.userName}
          </p>
          {action === "reject" && (
            <Input.TextArea
              placeholder="Reason for rejection (optional)"
              rows={3}
              style={{ marginTop: 8 }}
              onChange={(e) => (remark = e.target.value)}
            />
          )}
        </div>
      ),
      okText: `Yes, ${action}`,
      okButtonProps: {
        danger: action === "reject",
        type: "primary",
      },
      cancelText: "Cancel",
      centered: true,
      onOk: () => {
        console.log("Confirmed:", action, withdrawal);
        handleWithdrawAction(withdrawal, action, remark);
      },
    });
  };

  const handleChange = (newPagination, filters) => {
    setPagination(newPagination);
    fetchData(newPagination, filters);
  };

  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      try {
        if (list.length === 0) {
          message.warning("No data to export.");
          return;
        }

        const dataToExport = list.map((tx) => ({
          "User Name": tx.userName || "User",
          "Transaction ID": tx.transaction_id || "-",
          Amount: tx.amount,
          "Transaction Type": tx.transaction_type || "-",
          "Type (Credit/Debit)": tx.type,
          Status: tx.status,
          Date: tx.date,
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");
        XLSX.writeFile(workbook, "Transactions_Export.xlsx");

        message.success(
          "Transaction data exported successfully! Please check the downloaded Excel file."
        );
      } catch (error) {
        console.error("Failed to export data", error);
        ShowToast("An error occurred during export.", Severty.ERROR);
      } finally {
        setIsExporting(false);
      }
    }, 100);
  };

  return (
    <Card>
      <Row justify="space-between" align="middle" className="mb-4">
        <Col>
          <Title level={4}>Wallet & Payment Manager</Title>
          <Text type="secondary">
            Monitor wallets, transactions, and manage withdrawals
          </Text>
        </Col>
        <Col>
          <div className="new-flex-exposa">
            {activeTab === "transactions" && (
              <Button
                icon={<DownloadOutlined />}
                type="primary"
                onClick={handleExport}
                loading={isExporting}
                disabled={isExporting}
              >
                Export Page
              </Button>
            )}
            <div className="main-new-class-flexx">
              <div className="pageHeadingSearch d-flex gap-2">
                {(activeTab === "wallets" ||
                  activeTab === "transactions" ||
                  activeTab === "withdrawals" ||
                  activeTab === "kyc") && (
                  <Input.Search
                    placeholder="Search by name, email"
                    allowClear
                    className="searchInput"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                )}
              </div>
              {activeTab === "transactions" && (
                <Select
                  value={typeFilter}
                  onChange={setTypeFilter}
                  style={{ width: 150 }}
                >
                  <Option value="all">All Types</Option>
                  <Option value="deposit">Deposit</Option>
                  <Option value="Signup Bonus">Signup Bonus</Option>
                  <Option value="Joined Quiz">Joined Quiz</Option>
                  <Option value="Withdraw">Withdrawal</Option>
                  <Option value="Quiz Earning">Quiz Earning</Option>
                  <Option value="Referral Bonus">Referral Bonus</Option>
                  <Option value="Purchase">Purchase</Option>
                  <Option value="Winning">Winning</Option>
                </Select>
              )}
              {(activeTab === "withdrawals" || activeTab === "kyc") && (
                <Select
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: 150 }}
                >
                  <Option value="all">All Status</Option>
                  <Option value="pending">Pending</Option>
                  <Option value="approved">Approved</Option>
                  <Option value="rejected">Rejected</Option>
                </Select>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Tabs
        className="main-new-tabs-class-098  main-new-tabs-class-098-mewk"
        activeKey={activeTab}
        onChange={setActiveTab}
        type="card"
      >
        <TabPane
          tab={
            <span>
              <DollarCircleOutlined /> Wallet Balances
            </span>
          }
          key="wallets"
        >
          <div className="table-responsive customPagination">
            <Table
              columns={columns.wallets}
              dataSource={list}
              rowKey={(record) => record.userId}
              loading={loading}
              pagination={{
                current: pagination.current,
                defaultPageSize: pagination.pageSize,
                responsive: true,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
              onChange={handleChange}
              className="ant-border-space"
            />
          </div>
        </TabPane>
        <TabPane
          tab={
            <span>
              <FileTextOutlined /> Transactions
            </span>
          }
          key="transactions"
        >
          <div className="table-responsive customPagination">
            <Table
              columns={columns.transactions}
              dataSource={list}
              rowKey={(record) => record.transaction_id || record._id}
              loading={loading}
              pagination={{
                current: pagination.current,
                defaultPageSize: pagination.pageSize,
                responsive: true,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
              onChange={handleChange}
              className="ant-border-space"
            />
          </div>
        </TabPane>
        <TabPane
          tab={
            <span>
              <FallOutlined /> Withdrawals
            </span>
          }
          key="withdrawals"
        >
          <div className="table-responsive customPagination">
            <Table
              columns={columns.withdrawals}
              dataSource={list}
              rowKey={(record) => record._id}
              loading={loading}
              pagination={{
                current: pagination.current,
                defaultPageSize: pagination.pageSize,
                responsive: true,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
              onChange={handleChange}
              className="ant-border-space"
            />
          </div>
        </TabPane>
        <TabPane
          tab={
            <span>
              <UserOutlined /> KYC Verification
            </span>
          }
          key="kyc"
        >
          {contextHolder}
          <div className="table-responsive customPagination">
            <Table
              columns={[
                {
                  title: "User Details",
                  render: (_, r) => (
                    <div>
                      <strong>{r.userName || "User"}</strong>
                      <br />
                      <small>{r.email}</small>
                      <br />
                    </div>
                  ),
                  width: 200,
                },
                {
                  title: "Bank Details",
                  render: (_, r) => (
                    <div>
                      {r.bankDetails?.bankName ? (
                        <>
                          <strong>Bank: {r.bankDetails.bankName}</strong>
                          <br />
                          <small>A/C: {r.bankDetails.accountNumber}</small>
                          <br />
                          <small>IFSC: {r.bankDetails.ifscCode}</small>
                        </>
                      ) : (
                        <div style={{ color: "#ff4d4f" }}>
                          Bank details not provided
                        </div>
                      )}
                    </div>
                  ),
                  width: 200,
                },
                {
                  title: "Documents",
                  render: (_, r) => (
                    <Space direction="vertical" size="small">
                      <div>
                        <strong>NIN Number:</strong>
                        <br />
                        {r.nin_number ? (
                          <Tag color="green">{r.nin_number}</Tag>
                        ) : (
                          <Tag color="red">Not provided</Tag>
                        )}
                      </div>
                      <div>
                        <strong>Passport:</strong>
                        <br />
                        {r.passport ? (
                          <img
                            src={r.passport}
                            alt="Passport"
                            style={{
                              width: 50,
                              height: 50,
                              objectFit: "cover",
                              borderRadius: 4,
                              border: "1px solid #d9d9d9",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              Modal.info({
                                title: "Passport Document",
                                content: (
                                  <img
                                    src={r.passport}
                                    alt="Passport"
                                    style={{ width: "100%", maxHeight: "70vh" }}
                                  />
                                ),
                                width: "80%",
                                maskClosable: true,
                              })
                            }
                          />
                        ) : (
                          <Tag color="red">Not provided</Tag>
                        )}
                      </div>
                    </Space>
                  ),
                  width: 200,
                },
                {
                  title: "Status & Date",
                  render: (_, r) => (
                    <Space direction="vertical" size="small">
                      {getStatusTag(r.kycStatus)}
                      <div>
                        <small>
                          <ClockCircleOutlined /> {r.date}
                        </small>
                      </div>
                    </Space>
                  ),
                  width: 150,
                },
                {
                  title: "Actions",
                  render: (_, r) => {
                    if (r.kycStatus === "Pending") {
                      return (
                        <Space>
                          <Tooltip title="Approve">
                            <Button
                              type="text"
                              icon={
                                <CheckCircleOutlined
                                  style={{ color: "#52c41a", fontSize: "18px" }}
                                />
                              }
                              onClick={() => showConfirm("approve", r.userId)}
                            />
                          </Tooltip>
                          <Tooltip title="Reject">
                            <Button
                              type="text"
                              icon={
                                <CloseCircleOutlined
                                  style={{ color: "#ff4d4f", fontSize: "18px" }}
                                />
                              }
                              onClick={() => showConfirm("reject", r.userId)}
                            />
                          </Tooltip>
                        </Space>
                      );
                    }
                    return null;
                  },
                  width: 120,
                },
              ]}
              dataSource={list}
              rowKey={(record) => record.userId}
              loading={loading}
              pagination={{
                current: pagination.current,
                defaultPageSize: pagination.pageSize,
                responsive: true,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "30", "50"],
              }}
              onChange={handleChange}
              className="ant-border-space"
              scroll={{ x: true }}
            />
          </div>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default WalletPaymentManager;
